import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import SearchBarComponent from "components/customs/searchbarV2";
import CustomDataTable from "components/customs/table";
import CommentsModal from "components/customs/comments-modal";
import { genericGet } from "services/services.general";
import { fetchComments } from "services/services.congressman";
import { defaultTableOption, rowPerPage } from "constants/table";
import { congRoles } from "utility/constants";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { HeaderText } from "assets/js/Styles";
import { getPassCode, getAccessToken } from "config";
import { Toast, getFormattedContactNumber } from "utility/helpers";
import { columnTable } from "./columns";
import CongressmanDetailsCard from "./DetailsCard";

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 50px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  margin-top: 16px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const TableHeaderSection = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;

const TableSection = styled.div`
  margin-top: 16px;
`;

const Main = () => {
  const navigate = useNavigate();

  const [congDetails, setCongDetails] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [recipientComments, setRecipientComments] = useState([]);
  const [editingCongressman, setEditingCongressman] = useState("");
  const [regions] = useState([]);

  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });

  let changeSearchTimeoutID;

  const { user } = useAuth();

  const role = user?.type;

  const isRoleCongressman = congRoles.includes(role);

  const dateRange = useSelector((state) => state.dateRange);
  const { dateFrom, dateTo } = dateRange || {};

  const toggleModal = () => setModalVisible(!modalVisible);

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 100,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column, sortDirection) => {
    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const fetchItems = () => {
    const passCode = getPassCode();

    if (!passCode) {
      Toast({
        type: 4,
        content: "Pass code not found. Please login using code login.",
      });

      setTableOption({
        ...tableOption,
        isLoading: false,
        data: [],
        totalRows: 0,
      });

      return;
    }

    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    const params = {
      limit: tableOption.currentRowsPerPage,
      offset: tableOption.offset,
      search: tableOption.searchValue,
      column: tableOption.sort.field,
      order: tableOption.sort.direction,
      startDate: dateFrom || "",
      endDate: dateTo || "",
    };

    const url = `/dpwhitem/${passCode}`;

    genericGet({
      url,
      params,
      headers: {
        "x-auth-token": getAccessToken(),
      },
      callback: (data) => {
        if (data) {
          // Handle response - adjust based on actual API response structure
          const items = Array.isArray(data)
            ? data
            : data?.rows || data?.data || [];
          const count = data?.count || data?.total || items.length;

          setTableOption({
            ...tableOption,
            isLoading: false,
            totalRows: count,
            data: [{ ...items }],
          });

          setCongDetails({
            name: items?.allocationContactName,
            position: items?.officialPosition,
            contact: getFormattedContactNumber(items?.allocationContactNumber),
          });
        } else {
          setTableOption({
            ...tableOption,
            isLoading: false,
            data: [],
            totalRows: 0,
          });
        }
      },
    });
  };

  const fetchRecipientMsgs = (rowId) => {
    fetchComments({
      params: rowId,
      headers: {
        "x-auth-token": getAccessToken(),
      },
      callback: (data) => {
        setRecipientComments(data);
      },
    });
  };

  const columns = columnTable({
    tableOption,
    setTableOption,
    role,
    regions,
    toggleModal,
    setEditingCongressman,
    fetchRecipientMsgs,
    congDetails,
    isRoleCongressman,
    navigate,
  });

  useEffect(() => {
    if (dateFrom) {
      fetchItems();
    }
  }, [
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.direction,
    tableOption.sort.field,
    dateFrom,
  ]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <>
      <HeaderSection>
        <HeaderText>{"DPWH Listing"}</HeaderText>
      </HeaderSection>
      <CongressmanDetailsCard congDetails={congDetails} />

      <CardSection>
        <CardContainer>
          <TableHeaderSection>
            <div className="position-relative">
              <SearchBarComponent
                onChangeSearch={onChangeSearch}
                isDisabled={tableOption?.isLoading}
              />
            </div>
          </TableHeaderSection>

          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationServer
              paginationTotalRows={tableOption.totalRows}
              progressPending={tableOption.isLoading}
              sortServer
              defaultSortField={tableOption.sort.field}
              defaultSortAsc={tableOption.sort.direction}
            />
          </TableSection>
        </CardContainer>
      </CardSection>

      <CommentsModal
        isOpen={modalVisible}
        handleClose={toggleModal}
        fetchComments={fetchRecipientMsgs}
        comments={recipientComments}
        congID={editingCongressman}
        congressmanId={editingCongressman}
        selectedAgency={null}
        handleFetchTable={fetchItems}
        view="others"
      />
    </>
  );
};

export default Main;
